import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getPlpTagFromUrl, isPlpRoute } from '@/features/plp/utils/plpUrl';

interface BrowseBannerVm {
  name: string;
  imageUrl: string;
  desktopImageUrl: string | null;
  landingUrl: string | null;
  bannerType: string;
}

interface BrowseBannerServiceInterface {
  getBrowseBanners(tags?: string): Promise<BrowseBannerVm[]>;
}

export interface UseBrowseBannerOptions {
  service: BrowseBannerServiceInterface;
}

export interface UseBrowseBannerResult {
  banner: BrowseBannerVm | null;
  isLoading: boolean;
}

export function useBrowseBanner({
  service,
}: UseBrowseBannerOptions): UseBrowseBannerResult {
  const location = useLocation();

  const [banner, setBanner] = useState<BrowseBannerVm | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Once a banner is loaded, keep it for this PLP visit so that filter URL
  // changes (e.g. /rings.html → /rings-earrings.html) don't re-trigger a
  // fetch, which would cause a shimmer blink and a new random banner pick.
  const hasLoadedRef = useRef(false);

  const tags = useMemo(() => {
    const pathname = location.pathname || '';
    if (!isPlpRoute(pathname)) return '';
    return getPlpTagFromUrl(pathname);
  }, [location.pathname]);

  useEffect(() => {
    // Skip re-fetch if a banner was already loaded in this component instance.
    if (hasLoadedRef.current) return;
    if (!tags) return;

    let isMounted = true;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- start async fetch; state updates happen only from promise callbacks
    setIsLoading(true);
    service
      .getBrowseBanners(tags)
      .then((banners) => {
        if (!isMounted || banners.length === 0) return;
        hasLoadedRef.current = true;
        const idx = Math.floor(Math.random() * banners.length);
        setBanner(banners[idx]);
      })
      .catch((error) => {
        console.error('Failed to load browse banners', error);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [tags, service]);

  return { banner, isLoading };
}
